import React, { useEffect, useState, useRef } from "react";
import { Select, Space, Button, Tag, Modal, Spin } from "antd";
import { EditOutlined, CloseOutlined } from "@ant-design/icons";
import { usePermissions } from "../../../PermissionsProvider";
import { SentinelService } from "../../../services/SentinelService";
import { Sentinel } from "../../../model_gen/sentinel";
import EditSentinelForm from "../../Sentinel/EditSentinelForm";
import { Application, ApplicationAction } from "../../../model_gen/application";
import StepHeader from "../StepHeader";

interface SelectSentinelsProps {
  application?: Application;
  intent?: ApplicationAction;
  onChange: (sentinels: Sentinel[]) => void;
}

const SelectSentinels: React.FC<SelectSentinelsProps> = ({
  application,
  intent,
  onChange,
}) => {
  const { apiKey } = usePermissions();
  const [sentinels, setSentinels] = useState<Sentinel[]>([]);
  const [selectedSentinels, setSelectedSentinels] = useState<string[]>([]);
  const [editedSentinels, setEditedSentinels] = useState<{
    [key: string]: Sentinel;
  }>({});
  const [loading, setLoading] = useState(false);
  const [isEditModalVisible, setIsEditModalVisible] = useState(false);
  const selectedSentinelRef = useRef<Sentinel | undefined>(undefined);
  const [selectKey, setSelectKey] = useState(0);

  useEffect(() => {
    if (application && intent) {
      setLoading(true);
      SentinelService.getSentinelsByIntentName(
        intent.action_type,
        null,
        apiKey
      ).subscribe(
        (fetchedSentinels: Sentinel[]) => {
          setSentinels(fetchedSentinels);
          const highSeveritySentinels = fetchedSentinels
            ?.filter((sentinel: Sentinel) => sentinel.severity >= 3)
            .map((sentinel: Sentinel) => sentinel.id.toString());
          setSelectedSentinels(highSeveritySentinels || []);
          setLoading(false);
        },
        (error: any) => {
          console.error("Error fetching sentinels:", error);
          setLoading(false);
        }
      );
    }
  }, [application, intent, apiKey]);

  useEffect(() => {
    const finalSentinels = selectedSentinels
      .map(
        (id) =>
          editedSentinels[id] || sentinels.find((s) => s.id.toString() === id)
      )
      ?.filter((s): s is Sentinel => s !== undefined);

    onChange(finalSentinels);
  }, [selectedSentinels, editedSentinels, sentinels]);

  const handleEditSentinel = (e: React.MouseEvent, sentinelId: string) => {
    e.preventDefault();
    e.stopPropagation();

    const sentinel =
      editedSentinels[sentinelId] ||
      sentinels.find((s) => s.id.toString() === sentinelId);

    if (sentinel) {
      selectedSentinelRef.current = sentinel;
      setIsEditModalVisible(true);
    }
  };

  const handleEditSubmit = (formData: Sentinel) => {
    setEditedSentinels((prev) => ({
      ...prev,
      [formData.id]: formData,
    }));
    setSelectKey((prev) => prev + 1);
    setIsEditModalVisible(false);
    selectedSentinelRef.current = undefined;
  };

  const isSentinelEdited = (sentinelId: string): boolean => {
    const editedSentinel = editedSentinels[sentinelId];
    if (!editedSentinel) return false;

    const originalSentinel = sentinels.find(
      (s) => s.id.toString() === sentinelId
    );
    if (!originalSentinel) return false;

    return (
      editedSentinel.sentinel_name !== originalSentinel.sentinel_name ||
      editedSentinel.sentinel_directive.directive_body.main !==
        originalSentinel.sentinel_directive.directive_body.main ||
      editedSentinel.sentinel_directive.directive_body.override !==
        originalSentinel.sentinel_directive.directive_body.override ||
      !arraysEqual(
        editedSentinel.action_types,
        originalSentinel.action_types
      ) ||
      editedSentinel.severity !== originalSentinel.severity ||
      !objectsEqual(editedSentinel.meta, originalSentinel.meta)
    );
  };

  const arraysEqual = (a: string[], b: string[]) =>
    a.length === b.length && a.every((val, index) => val === b[index]);

  const objectsEqual = (a: any, b: any): boolean =>
    JSON.stringify(a) === JSON.stringify(b);

  const getSentinelName = (sentinelId: string): string => {
    const editedSentinel = editedSentinels[sentinelId];
    if (editedSentinel) {
      return editedSentinel.sentinel_name;
    }
    const originalSentinel = sentinels.find(
      (s) => s.id.toString() === sentinelId
    );
    return originalSentinel ? originalSentinel.sentinel_name : "";
  };

  const renderSelectedSentinel = (props: any) => {
    const { value, closable, onClose } = props;
    const isEdited = isSentinelEdited(value);
    const sentinelName = getSentinelName(value);

    return (
      <Tag
        closable={closable}
        onClose={onClose}
        style={{ marginRight: 3 }}
        onClick={(e) => e.preventDefault()}>
        <span style={{ marginRight: 8 }}>
          {sentinelName}
          {isEdited && (
            <span
              style={{
                color: "#21b892",
                fontWeight: "bold",
                fontSize: "16px",
                marginLeft: "2px",
              }}>
              *
            </span>
          )}
        </span>
        <Button
          icon={<EditOutlined />}
          size="small"
          type="text"
          onClick={(e) => handleEditSentinel(e, value)}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        />
      </Tag>
    );
  };

  if (loading) {
    return (
      <Space direction="vertical" size="large" style={{ width: "100%" }}>
        <StepHeader
          title="Select Sentinels"
          description="Choose and configure the sentinels for evaluation"
        />
        <Spin />
      </Space>
    );
  }

  return (
    <Space direction="vertical" size="large" style={{ width: "100%" }}>
      <StepHeader
        title="Select Sentinels"
        description="Choose and configure the sentinels for evaluation"
      />

      <Select
        key={selectKey}
        mode="multiple"
        style={{ width: "100%", maxWidth: "800px" }}
        value={selectedSentinels}
        onChange={(value) => setSelectedSentinels(value)}
        placeholder="Select sentinels"
        optionLabelProp="label"
        tagRender={renderSelectedSentinel}
        disabled={!application || !intent}
        size="large">
        {sentinels.map((sentinel) => (
          <Select.Option
            key={sentinel.id}
            value={sentinel.id.toString()}
            label={sentinel.sentinel_name}>
            {sentinel.sentinel_name}
          </Select.Option>
        ))}
      </Select>

      <Modal
        open={isEditModalVisible}
        onCancel={() => {
          setIsEditModalVisible(false);
          selectedSentinelRef.current = undefined;
        }}
        footer={null}
        width={800}
        title="Sentinel"
        closeIcon={<CloseOutlined />}>
        {selectedSentinelRef.current && (
          <EditSentinelForm
            sentinel={selectedSentinelRef.current}
            loading={false}
            isDataLoaded={true}
            onSubmit={handleEditSubmit}
            onClose={() => {
              setIsEditModalVisible(false);
              selectedSentinelRef.current = undefined;
            }}
          />
        )}
      </Modal>
    </Space>
  );
};

export default SelectSentinels;
